import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { Tpl3plService, ExportQueueItem } from '../../../services/tpl3pl.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { RawResponseModalComponent } from '../shared/raw-response-modal/raw-response-modal.component';

export interface RawPushResultRow {
	orderno: string;
	success: boolean;
	message: string;
	fullResult: any;
}

@Component({
	selector: 'app-tpl3pl-export-queue',
	templateUrl: './tpl3pl-export-queue.component.html',
	styleUrls: ['./tpl3pl-export-queue.component.scss']
})
export class Tpl3plExportQueueComponent implements OnInit, AfterViewInit {
	@ViewChild(MatPaginator) paginator!: MatPaginator;
	@ViewChild(MatSort) sort!: MatSort;

	displayedColumns: string[] = ['orderno', 'tpl3pl_remote_order_id', 'export_status', 'created_at', 'export_attempts', 'last_export_attempt', 'error_message', 'actions'];
	dataSource = new MatTableDataSource<ExportQueueItem>();
	private allItems: ExportQueueItem[] = [];
	loading = false;
	processing = false;
	exportingOrderNo: string | null = null;
	statusFilter = '';
	orderFilter = '';
	includeArchived = false;
	lastExportResult: any = null;
	previewLoadingOrderNo: string | null = null;
	selectedErrorItem: ExportQueueItem | null = null;
	selectedErrorDetails: {
		message: string;
		errorCode?: string;
		canRetry: boolean;
		response?: any;
		circuitOpen?: boolean;
	} | null = null;
	rawPushInProgress = false;
	rawPushProgress = '';
	rawPushLimit = 10;
	rawPushResults: RawPushResultRow[] = [];
	rawPushResultsDataSource = new MatTableDataSource<RawPushResultRow>([]);
	rawPushResultsColumns: string[] = ['orderno', 'status', 'message', 'view_raw'];
	get eligibleOrdersCount(): number {
		const data = this.dataSource.data || [];
		return data.filter((r: ExportQueueItem) => r.export_status === 'pending' || r.export_status === 'failed').length;
	}
	constructor(
		private tpl3plService: Tpl3plService,
		private globalSearchService: GlobalSearchService,
		private router: Router,
		private route: ActivatedRoute,
		private dialog: MatDialog
	) {}

	ngOnInit(): void {
		this.loadQueue();
	}

	ngAfterViewInit(): void {
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;
	}

	queueTotal: number | null = null;

	loadQueue(): void {
		this.loading = true;
		this.selectedErrorItem = null;
		this.selectedErrorDetails = null;
		if (this.dataSource.paginator) {
			this.dataSource.paginator.firstPage();
		}
		const filters: any = { limit: 100, offset: 0, includeArchived: this.includeArchived };
		if (this.statusFilter) {
			filters.status = this.statusFilter;
		}

		this.tpl3plService.getExportQueue(filters).subscribe({
			next: (result) => {
				this.allItems = result.data || [];
				this.queueTotal = result.total ?? null;
				this.applyFilters();
				this.loading = false;
			},
			error: () => {
				this.globalSearchService.showNotification('Error loading export queue', 'danger', 'bottom', 'right');
				this.loading = false;
			}
		});
	}

	onStatusChange(): void {
		this.lastExportResult = null;
		this.loadQueue();
	}

	applyOrderFilter(): void {
	this.lastExportResult = null;
		this.applyFilters();
	}

	refreshQueue(): void {
	this.lastExportResult = null;
		this.statusFilter = '';
		this.orderFilter = '';
		this.loadQueue();
	}

dismissLastResult(): void {
	this.lastExportResult = null;
}

	private applyFilters(): void {
		let rows = this.allItems.slice();
		const orderFilterValue = (this.orderFilter || '').trim().toLowerCase();
		if (orderFilterValue) {
			rows = rows.filter(r =>
				(r.orderno || '')
					.toString()
					.toLowerCase()
					.includes(orderFilterValue)
			);
		}

		this.dataSource.data = rows;
		if (this.dataSource.paginator) {
			this.dataSource.paginator.firstPage();
		}
	}

	processQueue(): void {
		this.processing = true;
		this.lastExportResult = null;
		this.tpl3plService.processQueue().subscribe({
			next: (result) => {
				this.loadQueue();
				if (result.success) {
					this.lastExportResult = result;
					this.globalSearchService.showNotification('Processed ' + (result.processed || 0) + ' orders', 'success', 'bottom', 'right');
				} else {
					this.lastExportResult = null;
					this.globalSearchService.showNotification('Batch had failures – see order rows', 'warning', 'bottom', 'right');
				}
				this.processing = false;
			},
			error: (error) => {
				this.loadQueue();
				this.lastExportResult = null;
				this.globalSearchService.showNotification('Error processing queue', 'warning', 'bottom', 'right');
				this.processing = false;
			}
		});
	}

	retryExport(item: ExportQueueItem): void {
		this.lastExportResult = null;
		this.exportingOrderNo = item.orderno;
		this.tpl3plService.retryExport(item.id, item.orderno).subscribe({
			next: (result) => {
				this.exportingOrderNo = null;
				this.loadQueue();
				if (result.success) {
					this.globalSearchService.showNotification('Order exported', 'success', 'bottom', 'right');
				} else {
					this.lastExportResult = null;
					this.globalSearchService.showNotification('See error on order row', 'warning', 'bottom', 'right');
				}
			},
			error: (error) => {
				this.exportingOrderNo = null;
				this.loadQueue();
				this.lastExportResult = null;
				this.globalSearchService.showNotification('See error on order row', 'warning', 'bottom', 'right');
			}
		});
	}

	exportOrder(orderno: string): void {
		this.lastExportResult = null;
		this.exportingOrderNo = orderno;
		this.tpl3plService.exportOrder(orderno).subscribe({
			next: (result) => {
				this.exportingOrderNo = null;
				this.loadQueue();
				if (result.success) {
					this.globalSearchService.showNotification('Order exported', 'success', 'bottom', 'right');
				} else {
					this.lastExportResult = null;
					this.globalSearchService.showNotification('See error on order row', 'warning', 'bottom', 'right');
				}
			},
			error: (error) => {
				this.exportingOrderNo = null;
				this.loadQueue();
				this.lastExportResult = null;
				this.globalSearchService.showNotification('See error on order row', 'warning', 'bottom', 'right');
			}
		});
	}

	getEligibleOrdersForRawPush(): ExportQueueItem[] {
		const data = this.dataSource.data || [];
		return data
			.filter((r: ExportQueueItem) => r.export_status === 'pending' || r.export_status === 'failed')
			.slice(0, this.rawPushLimit);
	}

	rawManualPush(): void {
		const eligible = this.getEligibleOrdersForRawPush();
		if (eligible.length === 0) {
			this.globalSearchService.showNotification('No pending or failed orders to push', 'warning', 'bottom', 'right');
			return;
		}
		this.rawPushInProgress = true;
		this.rawPushResults = [];
		this.rawPushResultsDataSource.data = [];
		const total = eligible.length;
		let index = 0;
		const run = () => {
			if (index >= eligible.length) {
				this.rawPushProgress = '';
				this.rawPushInProgress = false;
				this.rawPushResultsDataSource.data = [...this.rawPushResults];
				this.loadQueue();
				const succeeded = this.rawPushResults.filter(r => r.success).length;
				const failed = this.rawPushResults.length - succeeded;
				this.globalSearchService.showNotification('Raw push finished: ' + succeeded + ' exported, ' + failed + ' failed', failed > 0 ? 'warning' : 'success', 'bottom', 'right');
				if (this.rawPushResults.length > 0) {
					this.dialog.open(RawResponseModalComponent, {
						width: '720px',
						maxWidth: '96vw',
						maxHeight: '90vh',
						data: {
							orderno: this.rawPushResults[this.rawPushResults.length - 1].orderno,
							export_status: this.rawPushResults[this.rawPushResults.length - 1].success ? 'exported' : 'failed',
							content: this.rawPushResults[this.rawPushResults.length - 1].fullResult,
							onSent: () => this.loadQueue()
						},
						panelClass: 'raw-response-dialog'
					});
				}
				return;
			}
			const item = eligible[index];
			this.rawPushProgress = (index + 1) + ' of ' + total;
			const obs = item.export_status === 'failed' && item.id
				? this.tpl3plService.retryExport(item.id, item.orderno)
				: this.tpl3plService.exportOrder(item.orderno);
			obs.subscribe({
				next: (result: any) => {
					const normalized = result && typeof result === 'object' ? result : { success: false, message: 'No response' };
					this.rawPushResults.push({
						orderno: item.orderno,
						success: !!normalized.success,
						message: normalized.message || (normalized.success ? 'Exported' : 'Failed'),
						fullResult: normalized
					});
					index++;
					run();
				},
				error: (err) => {
					this.rawPushResults.push({
						orderno: item.orderno,
						success: false,
						message: err?.error?.message || err?.message || 'Request failed',
						fullResult: { success: false, message: err?.error?.message || err?.message }
					});
					index++;
					run();
				}
			});
		};
		run();
	}

	dismissRawPushResults(): void {
		this.rawPushResults = [];
		this.rawPushResultsDataSource.data = [];
	}

	openRawResponseForResult(row: RawPushResultRow): void {
		this.dialog.open(RawResponseModalComponent, {
			width: '720px',
			maxWidth: '96vw',
			maxHeight: '90vh',
			data: {
				orderno: row.orderno,
				export_status: row.success ? 'exported' : 'failed',
				content: row.fullResult,
				onSent: () => this.loadQueue()
			},
			panelClass: 'raw-response-dialog'
		});
	}

	onRawPushLimitChange(): void {}

	getStatusBadgeClass(status: string): string {
		switch (status) {
		case 'exported': return 'badge-success';
		case 'failed': return 'badge-danger';
		case 'exporting': return 'badge-warning';
		case 'dead_letter': return 'badge-secondary';
		case 'cancelled': return 'badge-warning';
		default: return 'badge-secondary';
		}
	}

	getStatusLabel(status: string): string {
		switch (status) {
		case 'pending': return 'Pending';
		case 'exporting': return 'Exporting';
		case 'exported': return 'Exported';
		case 'failed': return 'Failed';
		case 'dead_letter': return 'Dead Letter';
		case 'cancelled': return 'Cancelled';
		default: return status || '—';
		}
	}

	private static readonly ERROR_SUMMARY_MAX = 72;

	getErrorSummary(item: ExportQueueItem): string {
		if (item.export_response?.circuit_open) {
			return 'Circuit breaker open';
		}
		const msg = (item.error_message || '').trim();
		if (!msg) {
			return '';
		}
		if (msg.length <= Tpl3plExportQueueComponent.ERROR_SUMMARY_MAX) {
			return msg;
		}
		const lower = msg.toLowerCase();
		if (lower.includes('not enough quantity') || lower.includes('in inventory')) {
			const skuLike = msg.match(/\b[A-Z0-9]{6,12}\b/g);
			const uniq = skuLike ? [...new Set(skuLike)] : [];
			if (uniq.length > 2) {
				return `Quantity short (${uniq.length} SKUs) — click for details`;
			}
			if (uniq.length === 2) {
				return `Quantity short: ${uniq.join(', ')} — click for details`;
			}
			if (uniq.length === 1) {
				return `Quantity short: ${uniq[0]} — click for details`;
			}
			return 'Quantity short — click for details';
		}
		return msg.slice(0, 65).trim() + '… — click for details';
	}

	private getErrorCode(item: ExportQueueItem): string | undefined {
		const response = item.export_response as any;
		if (response && typeof response === 'object') {
			if (typeof response.error_code === 'string' && response.error_code) {
				return response.error_code;
			}
			if (typeof response.code === 'string' && response.code) {
				return response.code;
			}
		}
		return undefined;
	}

	canRetry(item: ExportQueueItem): boolean {
		if (item.export_status !== 'failed') {
			return false;
		}
		const errorCode = this.getErrorCode(item);
		if (!errorCode) {
			return true;
		}
		const unrecoverable = ['NO_ITEMS', 'STATUS_BLOCKED', 'STATUS_NOT_ELIGIBLE', 'STATUS_ALREADY_SHIPPED'];
		return unrecoverable.indexOf(errorCode) === -1;
	}

	openErrorDetails(item: ExportQueueItem): void {
		if (!item.error_message && !item.export_response) {
			this.selectedErrorItem = null;
			this.selectedErrorDetails = null;
			return;
		}

		const errorCode = this.getErrorCode(item);
		const canRetry = this.canRetry(item);
		const response = item.export_response as any;

		this.selectedErrorItem = item;
		this.selectedErrorDetails = {
			message: item.error_message || (response && (response.message as string)) || '',
			errorCode,
			canRetry,
			response: response && (response.response ?? response.raw_response ?? response),
			circuitOpen: !!(response && response.circuit_open)
		};
	}

	clearErrorDetails(): void {
		this.selectedErrorItem = null;
		this.selectedErrorDetails = null;
	}

	openResponseDetails(item: ExportQueueItem): void {
		if (!item) { return; }
		if (item.export_response) {
			this.dialog.open(RawResponseModalComponent, {
				width: '720px',
				maxWidth: '96vw',
				maxHeight: '90vh',
				data: {
					orderno: item.orderno,
					export_status: item.export_status,
					content: item.export_response,
					onSent: () => this.loadQueue()
				},
				panelClass: 'raw-response-dialog'
			});
			return;
		}
		this.previewLoadingOrderNo = item.orderno;
		this.tpl3plService.getExportPreview(item.orderno).subscribe({
			next: (res) => {
				this.previewLoadingOrderNo = null;
				if (res.success && res.request_payload) {
					this.dialog.open(RawResponseModalComponent, {
						width: '720px',
						maxWidth: '96vw',
						maxHeight: '90vh',
						data: {
							orderno: item.orderno,
							export_status: item.export_status,
							content: { request_payload: res.request_payload },
							onSent: () => this.loadQueue()
						},
						panelClass: 'raw-response-dialog'
					});
				} else {
					this.globalSearchService.showNotification(res.message || 'Could not load preview', 'warning', 'bottom', 'right');
				}
			},
			error: (err) => {
				this.previewLoadingOrderNo = null;
				this.globalSearchService.showNotification(err?.error?.message || 'Could not load preview', 'danger', 'bottom', 'right');
			}
		});
	}

	archiveQueueItem(item: ExportQueueItem): void {
		if (item.export_status !== 'exported') { return; }
		this.tpl3plService.archiveQueueItem(item.id).subscribe({
			next: (result) => {
				if (result.success) {
					this.globalSearchService.showNotification('Order archived', 'success', 'bottom', 'right');
					this.loadQueue();
				} else {
					this.globalSearchService.showNotification(result.message || 'Could not archive', 'danger', 'bottom', 'right');
				}
			},
			error: (err) => this.globalSearchService.showNotification(err?.error?.message || 'Error archiving', 'danger', 'bottom', 'right')
		});
	}

	archiveAllExported(): void {
		this.tpl3plService.archiveAllExported().subscribe({
			next: (result) => {
				if (result.success) {
					this.globalSearchService.showNotification(result.message || 'Exported orders archived', 'success', 'bottom', 'right');
					this.loadQueue();
				} else {
					this.globalSearchService.showNotification(result.message || 'Could not archive', 'danger', 'bottom', 'right');
				}
			},
			error: (err) => this.globalSearchService.showNotification(err?.error?.message || 'Error archiving', 'danger', 'bottom', 'right')
		});
	}

	viewLogs(item: ExportQueueItem): void {
		this.navigateToTab('logs', item.orderno);
	}

	viewShipments(item: ExportQueueItem): void {
		this.navigateToTab('shipments', item.orderno);
	}

	private navigateToTab(tab: 'logs' | 'shipments', orderno: string): void {
		this.router.navigate(['../' + tab], { relativeTo: this.route, queryParams: { orderno } });
	}

	deleteQueueItem(item: ExportQueueItem): void {
		this.tpl3plService.deleteQueueItem(item.id).subscribe({
			next: (result) => {
				if (result.success) {
					this.globalSearchService.showNotification('Queue item removed', 'success', 'bottom', 'right');
					this.allItems = this.allItems.filter(i => i.id !== item.id);
					this.applyFilters();
					if (this.selectedErrorItem?.id === item.id) {
						this.clearErrorDetails();
					}
				} else {
					this.globalSearchService.showNotification(result.message || 'Could not remove item', 'danger', 'bottom', 'right');
				}
			},
			error: (error) => {
				this.globalSearchService.showNotification(error?.error?.message || 'Error removing queue item', 'danger', 'bottom', 'right');
			}
		});
	}

	viewOrder(orderno: string): void {
		if (!orderno) { return; }
		this.router.navigate(['/orders/order-inquiry'], { queryParams: { orderno } });
	}
}
